// src/components/QuizTable.js

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

/**
 * Styled component for responsive tables.
 */
const StyledTable = styled.table`
  width: 100%;
  max-width: 1200px;
  border-collapse: collapse;
  margin: 20px auto;

  th,
  td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: left;
  }

  th {
    background-color: #f4f4f4;
  }

  /* Responsive table */
  @media (max-width: ${(props) => (props.scale)}px) {
    table,
    thead,
    tbody,
    th,
    td,
    tr {
      display: block;
    }

    thead {
      display: none; /* Hide table headers */
    }

    tr {
      margin-bottom: 1rem;
    }

    td {
      position: relative;
      padding-left: 50%;
      padding-top: 1.5em; /* Added padding to prevent overlap */
      padding-bottom: 1.5em; /* Added padding to prevent overlap */
      text-align: left; /* Changed to left for better readability */
      border: none;
      border-bottom: 1px solid #ddd;
      display:flex;
      align-items:center;
      height:100%;
    }

    td::before {
      content: attr(data-label);
      position: absolute;
      left: 0;
      top: 0;
      text-align: left;
      display:flex;
      height:100%;
      align-items:center;
      width: 45%;
      padding-right: 10px;
      white-space: normal; /* Allow wrapping */
      overflow-wrap: break-word; /* Handle long words */
      word-break: break-word; /* Additional break handling */
      font-weight: bold;
      margin-left: 10px;
      vertical-align: middle;
      margin-bottom: 0.5em; /* Added margin to separate label from content */
    }
  }
`;

/**
 * QuizTable Component
 * Enhances markdown tables with responsive labels.
 *
 * @param {object} props - Component props
 * @param {object} props.children - Children elements (Markdown table)
 * @returns {JSX.Element} Rendered table
 */
const QuizTable = ({ children }) => {
  const [localScaleFactor, setLocalScaleFactor] = useState(1);
  // Convert children to an array to handle both single and multiple children
  const childrenArray = React.Children.toArray(children);

  // Find the <thead> and <tbody> elements
  const thead = childrenArray.find(child => child.type === 'thead');
  const tbody = childrenArray.find(child => child.type === 'tbody');
  const params = new URLSearchParams(window.location.search);
  const scaleParam = params.get("scale");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const scaleParam = params.get('scale');
    const scale = parseFloat(scaleParam);

    if (!isNaN(scale)) {
      setLocalScaleFactor(scale * 600);
    }else {
      setLocalScaleFactor(1 * 600);
    }
  }, []); // Run only once when the component mounts


  // Initialize headerTexts
  let headerTexts = [];

  // Extract header texts if <thead> exists
  if (thead && thead.props && thead.props.children) {
    const headerRow = React.Children.toArray(thead.props.children).find(child => child.type === 'tr');
    if (headerRow && headerRow.props && headerRow.props.children) {
      headerTexts = React.Children.toArray(headerRow.props.children).map(header => {
        // Handle different header content types
        if (typeof header.props.children === 'string') {
          return header.props.children;
        } else if (Array.isArray(header.props.children)) {
          // Join array of children if header has multiple elements (e.g., strong tags)
          return header.props.children.map(child => (typeof child === 'string' ? child : '')).join('');
        }
        return '';
      });
    }
  }

  // Handle tables without headers by assigning default labels or skipping
  if (headerTexts.length === 0) {
    // Option 1: Skip data-labels and render the table as is
    return <StyledTable scale={localScaleFactor}>{children}</StyledTable>;

    // Option 2: Assign default labels (Uncomment below to use)
    /*
    const firstRow = childrenArray.find(child => child.type === 'tbody')?.props.children[0];
    const defaultHeaderCount = firstRow ? React.Children.count(firstRow.props.children) : 0;
    headerTexts = Array.from({ length: defaultHeaderCount }, (_, i) => `Column ${i + 1}`);

    // Proceed to assign data-labels as usual
    */
  }

  // Extract rows from <tbody>
  let rows = [];
  if (tbody && tbody.props && tbody.props.children) {
    rows = React.Children.toArray(tbody.props.children).filter(child => child.type === 'tr');
  }

  // Render table rows with data-labels
  const enhancedRows = rows.map((row, rowIndex) => {
    const cells = React.Children.toArray(row.props.children).map((cell, cellIndex) => (
      <td key={cellIndex} data-label={headerTexts[cellIndex] || ''}>
        {cell.props.children}
      </td>
    ));
    return <tr key={rowIndex}>{cells}</tr>;
  });

  return (
    <StyledTable scale={localScaleFactor}>
      <thead>
        <tr>
          {headerTexts.map((header, index) => (
            <th key={index}>{header}</th>
          ))}
        </tr>
      </thead>
      <tbody>{enhancedRows}</tbody>
    </StyledTable>
  );
};

QuizTable.propTypes = {
  children: PropTypes.node.isRequired,
};

export default QuizTable;
